/**
 * SegmentedControl, Tabs and ToolbarToggles.
 *
 * Three controls that look similar and mean different things, so each takes the
 * ARIA role that matches what it actually does:
 *
 *   * **SegmentedControl** picks one value out of a small set — a clef, a mode,
 *     an accidental. Nothing is revealed or hidden; a value simply changes. That
 *     is a radio group, so it is `radiogroup` / `radio` with `aria-checked`.
 *   * **Tabs** move between views, so they are `tablist` / `tab` wired by
 *     `aria-controls` to a `tabpanel`. A tab without a panel is a lie told to a
 *     screen reader, which is why `TabPanel` ships alongside.
 *   * **ToolbarToggles** are independent pressed states in a `toolbar` — a pen is
 *     held down, not chosen from a form. They use `aria-pressed`.
 *
 * All three share one keyboard contract from the WAI-ARIA practices: arrows move
 * within the group, Home and End jump to the ends, and only the active option is
 * in the tab order, so Tab moves past the group rather than through every item.
 */

import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export interface Option<T extends string> {
  value: T;
  label: string;
  icon?: IconName;
  /** Secondary line, used by the app's top-level tabs. */
  hint?: ReactNode;
}

function useRovingFocus(count: number) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (event: KeyboardEvent, index: number, select: (next: number) => void) => {
    const keys: Record<string, number> = {
      ArrowRight: index + 1,
      ArrowDown: index + 1,
      ArrowLeft: index - 1,
      ArrowUp: index - 1,
      Home: 0,
      End: count - 1,
    };
    const target = keys[event.key];
    if (target === undefined) return;
    event.preventDefault();
    const wrapped = (target + count) % count;
    select(wrapped);
    refs.current[wrapped]?.focus();
  };

  return { refs, onKeyDown };
}

export interface SegmentedControlProps<T extends string> {
  label: string;
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
}

/**
 * One value out of a set. Radio-group semantics, because that is what choosing
 * a clef or an accidental is — nothing is revealed, a value changes.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: SegmentedControlProps<T>) {
  const { refs, onKeyDown } = useRovingFocus(options.length);

  return (
    <div className="mm-segmented" role="radiogroup" aria-label={label}>
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            // Only the checked option is reachable by Tab; arrows move inside.
            tabIndex={selected ? 0 : -1}
            className="mm-segmented__option"
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index, (next) => onChange(options[next].value))}
          >
            {option.icon ? <Icon name={option.icon} size={17} /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export interface ToolbarTogglesProps<T extends string> {
  label: string;
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
}

/**
 * A tool palette. Each option is a pressed button inside a toolbar, which is how
 * a pen or an eraser actually behaves — held down, not selected from a form.
 * Selection carries a second, non-colour cue (see `.mm-tool-toggle` in the CSS).
 */
export function ToolbarToggles<T extends string>({
  label,
  options,
  value,
  onChange,
}: ToolbarTogglesProps<T>) {
  const { refs, onKeyDown } = useRovingFocus(options.length);

  return (
    <div className="mm-toolbar" role="toolbar" aria-label={label}>
      {options.map((option, index) => {
        const pressed = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            className="mm-tool-toggle"
            aria-pressed={pressed}
            title={option.label}
            tabIndex={pressed ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index, (next) => onChange(options[next].value))}
          >
            {option.icon ? <Icon name={option.icon} size={18} /> : null}
            <span className="mm-tool-toggle__label">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export interface TabsProps<T extends string> {
  label: string;
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Shared prefix for the generated tab and panel ids. */
  idPrefix?: string;
}

/** Id helpers so a tab and its panel can find each other. */
export const tabId = (prefix: string, value: string) => `${prefix}-tab-${value}`;
export const tabPanelId = (prefix: string, value: string) => `${prefix}-panel-${value}`;

export function Tabs<T extends string>({
  label,
  options,
  value,
  onChange,
  idPrefix = 'mm',
}: TabsProps<T>) {
  const { refs, onKeyDown } = useRovingFocus(options.length);

  return (
    <div className="mm-tabs" role="tablist" aria-label={label}>
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={tabId(idPrefix, option.value)}
            aria-selected={selected}
            // Wired to the panel below, so a tab is not a claim without a target.
            aria-controls={tabPanelId(idPrefix, option.value)}
            tabIndex={selected ? 0 : -1}
            className="mm-tab"
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index, (next) => onChange(options[next].value))}
          >
            <span className="mm-tab__label">{option.label}</span>
            {option.hint ? <span className="mm-tab__hint">{option.hint}</span> : null}
          </button>
        );
      })}
    </div>
  );
}


export interface TabPanelProps {
  idPrefix?: string;
  value: string;
  /** Keep every aria-controls target mounted while hiding inactive content. */
  active?: boolean;
  children: ReactNode;
}

/**
 * The other half of `Tabs`.
 *
 * `tabIndex={0}` is deliberate: when a panel holds no focusable control, a
 * keyboard user must still be able to reach its content after leaving the tab.
 */
export function TabPanel({ idPrefix = 'mm', value, active = true, children }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      id={tabPanelId(idPrefix, value)}
      aria-labelledby={tabId(idPrefix, value)}
      tabIndex={0}
      hidden={!active}
      className="mm-tabpanel"
    >
      {children}
    </div>
  );
}
